import { TempoElement } from "@/utils/tempoElement";
import { v4 as uuidv4 } from "uuid";

export const WRAP_IN_DIV_PLACEHOLDER_CODEBASE_ID =
  "tempo-wrap-in-div-placeholder";
export const DUPLICATE_PLACEHOLDER_PREFIX = "tempo-duplicate-placeholder-";

// Matches the file in tempo-devtools
export enum StylingFramework {
  INLINE = "Inline",
  CSS = "CSS",
  TAILWIND = "Tailwind",
}

type ClassType = {
  tailwind: string;
  css: string;
};

export enum ChangeType {
  STYLING = "STYLING",
  ADD_JSX = "ADD_JSX",
  MOVE_JSX = "MOVE_JSX",
  REMOVE_JSX = "REMOVE_JSX",
  CHANGE_PROP = "CHANGE_PROP",
  ADD_CLASS = "ADD_CLASS",
  REMOVE_CLASS = "REMOVE_CLASS",
  UPDATE_CLASSES = "UPDATE_CLASSES",
  EDIT_TEXT = "EDIT_TEXT",
  WRAP_DIV = "WRAP_DIV",
  CHANGE_TAG = "CHANGE_TAG",
  DUPLICATE = "DUPLICATE",
  UNDO = "UNDO",
  REDO = "REDO",
  UNKNOWN = "UNKNOWN",
}

// Make sure to match this in both tempo-devtools & ** tempo-api ** (in the undo/redo file)
export const CHANGE_TYPES_WITH_INSTANT_UNDO = [
  ChangeType.REMOVE_JSX,
  ChangeType.ADD_CLASS,
  ChangeType.STYLING,
  ChangeType.UPDATE_CLASSES,
];

export abstract class ChangeLedgerItem<T> {
  public id: string;
  public type: ChangeType;
  public changeName: string;
  public indexInLedger?: number;
  public changeFields: T;

  public canInstantUpdateWhileFlushing: boolean;

  public asUndoOfActivityId?: string;
  public asRedoOfActivityId?: string;

  // These two fields are set after the API request completes
  public activityId?: string;
  public prevIdToNewIdMap: { [prevId: string]: string } = {};

  public changeUndone?: boolean;
  public toastId?: string | number;
  public showedToastInsteadOfInstantUpdate?: boolean;

  private _consumed: boolean;
  private _failed: boolean;

  private _instantUpdateSent: boolean;
  private _instantUpdateFinished: boolean;
  private _instantUpdateSuccessful: boolean;
  private _sendInstantUpdate: boolean;

  // Extra data given after the instant update (e.g. the inner HTML for deleted elements)
  private _instantUpdateData?: any;

  // After instant update, which selected element key & multiselected element keys to set
  // Note -> we are not using callbacks for the undo instant update because we store this in
  // local storage then deserialize it, so the callbacks are lost
  // Also Note -> these element keys are selected explicitly in tempo-devtools
  // where the instant updates are performed (change item functions)

  // For instant update
  // Null will be set, while undefined will be ignored
  private elementKeyToSelectAfterInstantUpdate?: string | null;
  private elementKeysToMultiselectAfterInstantUpdate?: string[] | null;

  // For undo instant update
  // Null will be set, while undefined will be ignored
  private elementKeyToSelectAfterUndoInstantUpdate?: string | null;
  private elementKeysToMultiselectAfterUndoInstantUpdate?: string[] | null;

  // Resolvers after API call

  private _resolveApi?: (data?: any) => void;
  private _rejectApi?: (reason?: any) => void;
  private _apiRejectionAdded?: boolean;

  private _apiPromise: Promise<void>;

  constructor(
    type: ChangeType,
    changeName: string,
    changeFields: T,
    id?: string
  ) {
    this.id = id || uuidv4();
    this.type = type;
    this.changeFields = changeFields;
    this.changeName = changeName;
    this._consumed = false;
    this._failed = false;
    this._instantUpdateSent = false;
    this._instantUpdateFinished = false;
    this._instantUpdateSuccessful = false;
    this._sendInstantUpdate = true;
    this.canInstantUpdateWhileFlushing = false;

    this._apiPromise = new Promise<void>((resolve, reject) => {
      this._resolveApi = resolve;
      this._rejectApi = reject;
    });
  }

  public resolveApi(data?: any) {
    this._resolveApi?.(data);
  }

  public rejectApi(reason?: any) {
    if (this._apiRejectionAdded) {
      this._rejectApi?.(reason);
    }
  }

  public needsToSendInstantUpdate() {
    return !this._instantUpdateSent && this._sendInstantUpdate;
  }

  public markInstantUpdateSent() {
    this._instantUpdateSent = true;
  }

  public getInstantUpdateSent() {
    return this._instantUpdateSent;
  }

  public markInstantUpdateFinished(
    instantUpdateData: any,
    instantUpdateSuccessful: boolean
  ) {
    this._instantUpdateFinished = true;
    this._instantUpdateSuccessful = instantUpdateSuccessful;
    this._instantUpdateData = instantUpdateData;
  }

  public getInstantUpdateData() {
    return this._instantUpdateData;
  }

  public wasInstantUpdateSuccessful() {
    return this._instantUpdateSuccessful;
  }

  public isInstantUpdateFinished() {
    return this._instantUpdateFinished;
  }

  public markProcessedSucceeded() {
    this._consumed = true;
  }

  public markProcessedFailed() {
    this._failed = true;
    this._consumed = true;
  }

  public isFailed() {
    return this._failed;
  }

  public needToProcessChange() {
    return !this._consumed;
  }

  public onApiResolve(onFulfilled: (data?: any) => void) {
    return this._apiPromise.then(onFulfilled);
  }

  public onApiReject(onRejected: (reason?: any) => void) {
    this._apiRejectionAdded = true;
    return this._apiPromise.catch(onRejected);
  }

  abstract prepareApiRequest(
    canvasId: string,
    treeElementLookup: { [codebaseId: string]: any }
  ): {
    urlPath: string;
    body: {
      [key: string]: any;
    };
    successToastMessage?: string;
  };

  public doNotSendInstantUpdate() {
    this._sendInstantUpdate = false;
  }

  // For selecting/deslecting new elements after instant updates

  public clearSelectedElementsAfterInstantUpdate() {
    this.elementKeyToSelectAfterInstantUpdate = null;
    this.elementKeysToMultiselectAfterInstantUpdate = null;
  }

  public setSelectedElementsAfterInstantUpdate(
    selectedElementKey: string,
    multiselectedElementKeys: string[] | null
  ) {
    this.elementKeyToSelectAfterInstantUpdate = selectedElementKey;
    this.elementKeysToMultiselectAfterInstantUpdate = multiselectedElementKeys;
  }

  public clearSelectedElementsAfterUndoInstantUpdate() {
    this.elementKeyToSelectAfterUndoInstantUpdate = null;
    this.elementKeysToMultiselectAfterUndoInstantUpdate = null;
  }

  public setSelectedElementsAfterUndoInstantUpdate(
    selectedElementKey: string,
    multiselectedElementKeys: string[]
  ) {
    this.elementKeyToSelectAfterUndoInstantUpdate = selectedElementKey;
    this.elementKeysToMultiselectAfterUndoInstantUpdate =
      multiselectedElementKeys;
  }

  public getElementKeyToSelectAfterInstantUpdate() {
    return this.elementKeyToSelectAfterInstantUpdate;
  }

  public getElementKeysToMultiselectAfterInstantUpdate() {
    return this.elementKeysToMultiselectAfterInstantUpdate;
  }

  public getElementKeyToSelectAfterUndoInstantUpdate() {
    return this.elementKeyToSelectAfterUndoInstantUpdate;
  }

  public getElementKeysToMultiselectAfterUndoInstantUpdate() {
    return this.elementKeysToMultiselectAfterUndoInstantUpdate;
  }

  abstract applyCodebaseIdChanges(prevIdToNewIdMap: {
    [prevId: string]: string;
  }): void;

  public applyAllCodebaseIdChanges(prevIdToNewIdMap: {
    [prevId: string]: string;
  }) {
    const getNewKey = (prevKey: string) => {
      if (!prevKey) {
        return null;
      }

      const tempoElement = TempoElement.fromKey(prevKey);
      const codebaseId = tempoElement.codebaseId;
      const newCodebaseId = prevIdToNewIdMap[codebaseId];

      if (newCodebaseId) {
        return new TempoElement(
          newCodebaseId,
          tempoElement.storyboardId,
          tempoElement.uniquePath
        ).getKey();
      }

      return null;
    };

    /*
     * Instant update fields
     */

    if (this.elementKeyToSelectAfterInstantUpdate) {
      const newElementKey = getNewKey(
        this.elementKeyToSelectAfterInstantUpdate
      );
      this.elementKeyToSelectAfterInstantUpdate =
        newElementKey || this.elementKeyToSelectAfterInstantUpdate;
    }

    if (this.elementKeysToMultiselectAfterInstantUpdate) {
      this.elementKeysToMultiselectAfterInstantUpdate =
        this.elementKeysToMultiselectAfterInstantUpdate?.map((key) => {
          const newKey = getNewKey(key);
          return newKey || key;
        });
    }

    /*
     * Undo instant update fields
     */
    if (this.elementKeyToSelectAfterUndoInstantUpdate) {
      const newElementKey = getNewKey(
        this.elementKeyToSelectAfterUndoInstantUpdate
      );
      this.elementKeyToSelectAfterUndoInstantUpdate =
        newElementKey || this.elementKeyToSelectAfterUndoInstantUpdate;
    }

    if (this.elementKeysToMultiselectAfterUndoInstantUpdate) {
      this.elementKeysToMultiselectAfterUndoInstantUpdate =
        this.elementKeysToMultiselectAfterUndoInstantUpdate?.map((key) => {
          const newKey = getNewKey(key);
          return newKey || key;
        });
    }

    this.applyCodebaseIdChanges(prevIdToNewIdMap);
  }
}

/************************************************************************
 * Styling Change
 ************************************************************************/

export interface StylingChangeFields {
  codebaseId: string;
  stylingChanges: { [cssKey: string]: string };

  // Currently only one supported
  stylingFramework: StylingFramework.TAILWIND;
  modifiers: string[];
  customProperties: ClassType[];
}

export class StylingChange extends ChangeLedgerItem<StylingChangeFields> {
  constructor(changeFields: StylingChangeFields, id?: string) {
    super(ChangeType.STYLING, "Styling", changeFields, id);

    // Allow instant updates while flushing
    this.canInstantUpdateWhileFlushing = true;
  }

  public prepareApiRequest(
    canvasId: string,
    treeElementLookup: { [codebaseId: string]: any }
  ) {
    const {
      codebaseId,
      stylingChanges,
      stylingFramework,
      modifiers,
      customProperties,
    } = this.changeFields;

    return {
      urlPath: `canvases/${canvasId}/parseAndMutate/mutate/styling`,
      body: {
        reactElement: treeElementLookup[codebaseId],
        styling: stylingChanges,
        stylingFramework,
        modifiers,
        customProperties,
      },
    };
  }

  public applyCodebaseIdChanges(prevIdToNewIdMap: {
    [prevId: string]: string;
  }) {
    const newCodebaseId = prevIdToNewIdMap[this.changeFields.codebaseId];

    if (newCodebaseId) {
      this.changeFields.codebaseId = newCodebaseId;
    }
  }
}

/************************************************************************
 * Add Jsx Change
 ************************************************************************/

export interface AddJsxChangeFields {
  codebaseIdToAddTo: string;
  beforeCodebaseId?: string;
  afterCodebaseId?: string;

  // The element to add
  addCodebaseId?: string;
  addNativeTag?: string;
  fileContentsToSourceFrom?: string;
  fileContentsSourceFilename?: string;

  propsToSet?: { [key: string]: any };

  // Used to track the storyboard in activity stream
  deletedStoryboardId?: string;

  // For instant update
  htmlForInstantUpdate?: string;

  // Extra files to create alongside the main component
  extraPathToContents?: { [path: string]: string };
}

export class AddJsxChange extends ChangeLedgerItem<AddJsxChangeFields> {
  constructor(changeFields: AddJsxChangeFields, id?: string) {
    super(ChangeType.ADD_JSX, "Add Element", changeFields, id);
  }

  public prepareApiRequest(
    canvasId: string,
    treeElementLookup: { [codebaseId: string]: any }
  ) {
    const {
      codebaseIdToAddTo,
      beforeCodebaseId,
      afterCodebaseId,
      addCodebaseId,
      addNativeTag,
      fileContentsToSourceFrom,
      fileContentsSourceFilename,
      propsToSet,
      deletedStoryboardId,
      htmlForInstantUpdate,
      extraPathToContents,
    } = this.changeFields;

    const body: any = {
      destinationElement: treeElementLookup[codebaseIdToAddTo],
      beforeElement: treeElementLookup[beforeCodebaseId || ""],
      afterElement: treeElementLookup[afterCodebaseId || ""],
      newElement: {},
      canvasId,
      deletedStoryboardId,
      fileContentsToSourceFrom,
      fileContentsSourceFilename,
      extraFiles: extraPathToContents,
    };

    if (addCodebaseId) {
      body.newElement = {
        ...treeElementLookup[addCodebaseId],
      };
    } else if (addNativeTag) {
      body.newElement["type"] = "native";
      body.newElement["nativeTag"] = addNativeTag;
      body.newElement["componentName"] = addNativeTag;
    }

    if (propsToSet) {
      body.newElement["propsToSet"] = propsToSet;
    }

    if (!Object.keys(body.newElement).length) {
      delete body.newElement;
    }

    const hasInstantUpdate = Boolean(htmlForInstantUpdate);
    body["hasInstantUpdate"] = hasInstantUpdate;

    return {
      urlPath: `canvases/${canvasId}/parseAndMutate/mutate/addJsxElement`,
      body,

      // Only show the success message if we do not have instant updates
      successToastMessage: hasInstantUpdate ? undefined : "Successfully added",
    };
  }

  public applyCodebaseIdChanges(prevIdToNewIdMap: {
    [prevId: string]: string;
  }) {
    const fieldsToApply = [
      "codebaseIdToAddTo",
      "beforeCodebaseId",
      "afterCodebaseId",
      "addCodebaseId",
    ];

    fieldsToApply.forEach((field) => {
      // @ts-ignore
      const newCodebaseId = prevIdToNewIdMap[this.changeFields[field]];

      if (newCodebaseId) {
        // @ts-ignore
        this.changeFields[field] = newCodebaseId;
      }
    });
  }
}

/************************************************************************
 * Move JSX Change
 ************************************************************************/

export interface MoveJsxChangeFields {
  codebaseIdToMoveTo: string;
  codebaseIdToMove: string;
  afterCodebaseId?: string;
  beforeCodebaseId?: string;

  expectedCurrentParentCodebaseId?: string;
}

export class MoveJsxChange extends ChangeLedgerItem<MoveJsxChangeFields> {
  constructor(changeFields: MoveJsxChangeFields, id?: string) {
    super(ChangeType.MOVE_JSX, "Move Element", changeFields, id);
  }

  public prepareApiRequest(
    canvasId: string,
    treeElementLookup: { [codebaseId: string]: any }
  ) {
    const {
      codebaseIdToMoveTo,
      codebaseIdToMove,
      afterCodebaseId,
      beforeCodebaseId,
      expectedCurrentParentCodebaseId,
    } = this.changeFields;

    return {
      urlPath: `canvases/${canvasId}/parseAndMutate/mutate/moveJsxElement`,
      body: {
        elementToMove: treeElementLookup[codebaseIdToMove],
        newContainerElement: treeElementLookup[codebaseIdToMoveTo],
        afterElement: treeElementLookup[afterCodebaseId || ""],
        beforeElement: treeElementLookup[beforeCodebaseId || ""],

        expectedCurrentParent:
          treeElementLookup[expectedCurrentParentCodebaseId || ""],
      },
    };
  }

  public applyCodebaseIdChanges(prevIdToNewIdMap: {
    [prevId: string]: string;
  }) {
    const fieldsToApply = [
      "codebaseIdToMoveTo",
      "codebaseIdToMove",
      "afterCodebaseId",
      "beforeCodebaseId",
      "expectedCurrentParentCodebaseId",
    ];

    fieldsToApply.forEach((field) => {
      // @ts-ignore
      const newCodebaseId = prevIdToNewIdMap[this.changeFields[field]];

      if (newCodebaseId) {
        // @ts-ignore
        this.changeFields[field] = newCodebaseId;
      }
    });
  }
}

/************************************************************************
 * Remove JSX Change
 ************************************************************************/

export interface RemoveJsxChangeFields {
  codebaseIdsToRemove: string[];
}

export class RemoveJsxChange extends ChangeLedgerItem<RemoveJsxChangeFields> {
  constructor(changeFields: RemoveJsxChangeFields, id?: string) {
    // Deduplicate the codebaseIdsToRemove
    changeFields.codebaseIdsToRemove = Array.from(
      new Set(changeFields.codebaseIdsToRemove)
    );
    super(ChangeType.REMOVE_JSX, "Delete Element", changeFields, id);
  }

  public prepareApiRequest(
    canvasId: string,
    treeElementLookup: { [codebaseId: string]: any }
  ) {
    const { codebaseIdsToRemove } = this.changeFields;

    return {
      urlPath: `canvases/${canvasId}/parseAndMutate/mutate/removeJsxElement`,
      body: {
        elementsToRemove: codebaseIdsToRemove
          .map((codebaseId: string) => treeElementLookup[codebaseId])
          .filter((element: any) => element),
      },
    };
  }

  public applyCodebaseIdChanges(prevIdToNewIdMap: {
    [prevId: string]: string;
  }) {
    this.changeFields.codebaseIdsToRemove =
      this.changeFields.codebaseIdsToRemove.map((codebaseId: string) => {
        const newCodebaseId = prevIdToNewIdMap[codebaseId];

        if (newCodebaseId) {
          return newCodebaseId;
        }

        return codebaseId;
      });
  }
}

/************************************************************************
 * Change Prop Change
 ************************************************************************/

export interface ChangePropChangeFields {
  codebaseIdToChange: string;
  propName: string;
  propValue: string;
}

export class ChangePropChange extends ChangeLedgerItem<ChangePropChangeFields> {
  constructor(changeFields: ChangePropChangeFields, id?: string) {
    super(ChangeType.CHANGE_PROP, "Change Prop", changeFields, id);
  }

  public prepareApiRequest(
    canvasId: string,
    treeElementLookup: { [codebaseId: string]: any }
  ) {
    const { codebaseIdToChange, propName, propValue } = this.changeFields;

    return {
      urlPath: `canvases/${canvasId}/parseAndMutate/mutate/changePropValue`,
      body: {
        elementToModify: treeElementLookup[codebaseIdToChange],
        propName,
        propValue,
      },
      successToastMessage: "Prop changed",
    };
  }

  public applyCodebaseIdChanges(prevIdToNewIdMap: {
    [prevId: string]: string;
  }) {
    const newCodebaseId =
      prevIdToNewIdMap[this.changeFields.codebaseIdToChange];

    if (newCodebaseId) {
      this.changeFields.codebaseIdToChange = newCodebaseId;
    }
  }
}

/************************************************************************
 * Wrap in div change
 ************************************************************************/

export interface WrapDivChangeFields {
  codebaseIdsToWrap: string[];
}

export class WrapDivChange extends ChangeLedgerItem<WrapDivChangeFields> {
  constructor(changeFields: WrapDivChangeFields, id?: string) {
    // Deduplicate the codebaseIdsToWrap
    changeFields.codebaseIdsToWrap = Array.from(
      new Set(changeFields.codebaseIdsToWrap)
    );
    super(ChangeType.WRAP_DIV, "Wrap In Div", changeFields, id);
  }

  public prepareApiRequest(
    canvasId: string,
    treeElementLookup: { [codebaseId: string]: any }
  ) {
    const { codebaseIdsToWrap } = this.changeFields;

    return {
      urlPath: `canvases/${canvasId}/parseAndMutate/mutate/wrapInDiv`,
      body: {
        reactElements: codebaseIdsToWrap.map(
          (codebaseId: string) => treeElementLookup[codebaseId]
        ),
      },
    };
  }

  public applyCodebaseIdChanges(prevIdToNewIdMap: {
    [prevId: string]: string;
  }) {
    this.changeFields.codebaseIdsToWrap =
      this.changeFields.codebaseIdsToWrap.map((codebaseId: string) => {
        const newCodebaseId = prevIdToNewIdMap[codebaseId];

        if (newCodebaseId) {
          return newCodebaseId;
        }

        return codebaseId;
      });
  }
}

/************************************************************************
 * Duplicate change
 ************************************************************************/

export interface DuplicateChangeFields {
  codebaseIdsToDuplicate: string[];
}

export class DuplicateChange extends ChangeLedgerItem<DuplicateChangeFields> {
  constructor(changeFields: DuplicateChangeFields, id?: string) {
    // Deduplicate the codebaseIdsToDuplicate
    changeFields.codebaseIdsToDuplicate = Array.from(
      new Set(changeFields.codebaseIdsToDuplicate)
    );
    super(ChangeType.DUPLICATE, "Duplicate", changeFields, id);
  }

  public prepareApiRequest(
    canvasId: string,
    treeElementLookup: { [codebaseId: string]: any }
  ) {
    const { codebaseIdsToDuplicate } = this.changeFields;

    return {
      urlPath: `canvases/${canvasId}/parseAndMutate/mutate/duplicate`,
      body: {
        reactElements: codebaseIdsToDuplicate.map(
          (codebaseId: string) => treeElementLookup[codebaseId]
        ),
      },
    };
  }

  public applyCodebaseIdChanges(prevIdToNewIdMap: {
    [prevId: string]: string;
  }) {
    this.changeFields.codebaseIdsToDuplicate =
      this.changeFields.codebaseIdsToDuplicate.map((codebaseId: string) => {
        const newCodebaseId = prevIdToNewIdMap[codebaseId];

        if (newCodebaseId) {
          return newCodebaseId;
        }

        return codebaseId;
      });
  }
}

/************************************************************************
 * Change Tag Change
 ************************************************************************/

export interface ChangeTagChangeFields {
  codebaseIdToChange: string;
  newTagName: string;
}

export class ChangeTagChange extends ChangeLedgerItem<ChangeTagChangeFields> {
  constructor(changeFields: ChangeTagChangeFields, id?: string) {
    super(ChangeType.CHANGE_TAG, "Change Tag Name", changeFields, id);
  }

  public prepareApiRequest(
    canvasId: string,
    treeElementLookup: { [codebaseId: string]: any }
  ) {
    const { codebaseIdToChange, newTagName } = this.changeFields;

    return {
      urlPath: `canvases/${canvasId}/parseAndMutate/mutate/changeElementTag`,
      body: {
        elementToModify: treeElementLookup[codebaseIdToChange],
        newTag: newTagName,
      },
    };
  }

  public applyCodebaseIdChanges(prevIdToNewIdMap: {
    [prevId: string]: string;
  }) {
    const newCodebaseId =
      prevIdToNewIdMap[this.changeFields.codebaseIdToChange];

    if (newCodebaseId) {
      this.changeFields.codebaseIdToChange = newCodebaseId;
    }
  }
}

/************************************************************************
 * Add class Change
 ************************************************************************/

export interface AddClassChangeFields {
  codebaseIdToAddClass: string;
  className: string;
  modifiers: string[];
  customProperties: ClassType[];
  addingTailwindClass?: boolean;

  // Used in instant updated
  cssEquivalent?: string;

  // Not in the change ledger, only used for dragging instant updates
  temporaryOnly?: boolean;
}

export class AddClassChange extends ChangeLedgerItem<AddClassChangeFields> {
  constructor(changeFields: AddClassChangeFields, id?: string) {
    super(ChangeType.ADD_CLASS, "Add Class", changeFields, id);

    this.canInstantUpdateWhileFlushing = true;
  }

  public prepareApiRequest(
    canvasId: string,
    treeElementLookup: { [codebaseId: string]: any }
  ) {
    const {
      codebaseIdToAddClass,
      className,
      addingTailwindClass,
      modifiers,
      customProperties,
    } = this.changeFields;

    return {
      urlPath: `canvases/${canvasId}/parseAndMutate/mutate/addClass`,
      body: {
        reactElement: treeElementLookup[codebaseIdToAddClass],
        className,
        stylingFramework: addingTailwindClass
          ? StylingFramework.TAILWIND
          : null,
        modifiers,
        customProperties,
      },
    };
  }

  public applyCodebaseIdChanges(prevIdToNewIdMap: {
    [prevId: string]: string;
  }) {
    const newCodebaseId =
      prevIdToNewIdMap[this.changeFields.codebaseIdToAddClass];

    if (newCodebaseId) {
      this.changeFields.codebaseIdToAddClass = newCodebaseId;
    }
  }
}

/************************************************************************
 * Remove class change
 ************************************************************************/

export interface RemoveClassChangeFields {
  codebaseIdToRemoveClass: string;
  className: string;
}

export class RemoveClassChange extends ChangeLedgerItem<RemoveClassChangeFields> {
  constructor(changeFields: RemoveClassChangeFields, id?: string) {
    super(ChangeType.REMOVE_CLASS, "Remove Class", changeFields, id);
  }

  public prepareApiRequest(
    canvasId: string,
    treeElementLookup: { [codebaseId: string]: any }
  ) {
    const { codebaseIdToRemoveClass, className } = this.changeFields;

    return {
      urlPath: `canvases/${canvasId}/parseAndMutate/mutate/removeClass`,
      body: {
        reactElement: treeElementLookup[codebaseIdToRemoveClass],
        className,
      },
    };
  }

  public applyCodebaseIdChanges(prevIdToNewIdMap: {
    [prevId: string]: string;
  }) {
    const newCodebaseId =
      prevIdToNewIdMap[this.changeFields.codebaseIdToRemoveClass];

    if (newCodebaseId) {
      this.changeFields.codebaseIdToRemoveClass = newCodebaseId;
    }
  }
}

/************************************************************************
 * Edit text change
 ************************************************************************/
export interface EditTextChangeFields {
  codebaseIdToEditText: string;
  newText: string;
  oldText?: string;
}

export class EditTextChange extends ChangeLedgerItem<EditTextChangeFields> {
  constructor(changeFields: EditTextChangeFields, id?: string) {
    super(ChangeType.EDIT_TEXT, "Edit Text", changeFields, id);
  }

  public prepareApiRequest(
    canvasId: string,
    treeElementLookup: { [codebaseId: string]: any }
  ) {
    const { codebaseIdToEditText, newText, oldText } = this.changeFields;

    return {
      urlPath: `canvases/${canvasId}/parseAndMutate/mutate/editText`,
      body: {
        element: treeElementLookup[codebaseIdToEditText],
        newText,
        oldText,
      },
    };
  }

  public applyCodebaseIdChanges(prevIdToNewIdMap: {
    [prevId: string]: string;
  }) {
    const newCodebaseId =
      prevIdToNewIdMap[this.changeFields.codebaseIdToEditText];

    if (newCodebaseId) {
      this.changeFields.codebaseIdToEditText = newCodebaseId;
    }
  }
}

export type AnyChangeLedgerItem = ChangeLedgerItem<any>;

/************************************************************************
 * Undo Change Type
 ************************************************************************/
export interface UndoChangeFields {
  changeToUndo: AnyChangeLedgerItem;
  matchingActivityFlushed?: boolean;
}

export class UndoChange extends ChangeLedgerItem<UndoChangeFields> {
  constructor(changeFields: UndoChangeFields, id?: string) {
    super(ChangeType.UNDO, "Undo", changeFields, id);

    if (changeFields.changeToUndo?.canInstantUpdateWhileFlushing) {
      this.canInstantUpdateWhileFlushing = true;
    }
  }

  public prepareApiRequest(
    canvasId: string,
    treeElementLookup: { [codebaseId: string]: any }
  ) {
    return {
      urlPath: `canvases/${canvasId}/parseAndMutate/activities/undoChangeToFiles`,
      body: {},
    };
  }

  public applyCodebaseIdChanges(prevIdToNewIdMap: {
    [prevId: string]: string;
  }) {
    // Do nothing
  }
}

/************************************************************************
 * Redo Change Type
 ************************************************************************/
export interface RedoChangeFields {
  changeToRedo: AnyChangeLedgerItem;
}

export class RedoChange extends ChangeLedgerItem<RedoChangeFields> {
  constructor(changeFields: RedoChangeFields, id?: string) {
    super(ChangeType.REDO, "Redo", changeFields, id);

    if (changeFields.changeToRedo?.canInstantUpdateWhileFlushing) {
      this.canInstantUpdateWhileFlushing = true;
    }
  }

  public prepareApiRequest(
    canvasId: string,
    treeElementLookup: { [codebaseId: string]: any }
  ) {
    return {
      urlPath: `canvases/${canvasId}/parseAndMutate/activities/redoChangeToFiles`,
      body: {},
    };
  }

  public applyCodebaseIdChanges(prevIdToNewIdMap: {
    [prevId: string]: string;
  }) {
    // Do nothing
  }
}

/************************************************************************
 * Update class change
 ************************************************************************/
export interface UpdateClassesChangeFields {
  codebaseIdToUpdateClass: string;

  newClasses: string[];
  oldClasses: string[]; // For undo

  // Involved classes (may needed to substitute for Tailwind JIT)
  involvedClasses: { tailwind: string; css: string; variant?: string | null }[];

  isInstantChangeOnly?: boolean;
}

export class UpdateClassesChange extends ChangeLedgerItem<UpdateClassesChangeFields> {
  constructor(changeFields: UpdateClassesChangeFields, id?: string) {
    super(ChangeType.UPDATE_CLASSES, "Update Classes", changeFields, id);

    this.canInstantUpdateWhileFlushing = true;
  }

  public prepareApiRequest(
    canvasId: string,
    treeElementLookup: { [codebaseId: string]: any }
  ) {
    const { codebaseIdToUpdateClass, newClasses, oldClasses } =
      this.changeFields;

    return {
      urlPath: `canvases/${canvasId}/parseAndMutate/mutate/updateClasses`,
      body: {
        reactElement: treeElementLookup[codebaseIdToUpdateClass],
        newClasses,
        oldClasses,
      },
    };
  }

  public applyCodebaseIdChanges(prevIdToNewIdMap: {
    [prevId: string]: string;
  }) {
    const newCodebaseId =
      prevIdToNewIdMap[this.changeFields.codebaseIdToUpdateClass];

    if (newCodebaseId) {
      this.changeFields.codebaseIdToUpdateClass = newCodebaseId;
    }
  }
}

/************************************************************************
 * Unknown Change Type
 *
 * This change type is created from the activity stream when the change
 * ledger goes out of sync with it. This allows the user to still undo
 * that change.
 *
 * This can happen if for example a canvas is being shared and another
 * user makes a change in between this users' changes
 ************************************************************************/
export interface UnknownChangeFields {}

export class UnknownChange extends ChangeLedgerItem<UnknownChangeFields> {
  constructor(changeFields: UnknownChangeFields, id?: string) {
    super(ChangeType.UNKNOWN, "", changeFields, id);
    // Do not process unknown changes
    this.markProcessedSucceeded();
    this.doNotSendInstantUpdate();
  }

  public prepareApiRequest(
    canvasId: string,
    treeElementLookup: { [codebaseId: string]: any }
  ) {
    throw Error("Unsupported operation");

    // For typing
    return {
      urlPath: ``,
      body: {},
    };
  }

  public applyCodebaseIdChanges(prevIdToNewIdMap: {
    [prevId: string]: string;
  }) {
    // Do nothing
  }
}

/**
 * When serializing a change ledger item to a plain JS object, the class functions
 * are lost. This recreates the change item that was lost
 */
export const reconstructChangeLedgerClass = (plainJsObject: {
  [key: string]: any;
}): AnyChangeLedgerItem | null => {
  if (!plainJsObject || !plainJsObject.type) {
    return null;
  }

  const changeType = plainJsObject.type as ChangeType;
  const changeFields = plainJsObject.changeFields;
  const id = plainJsObject.id;

  const getChangeForType = () => {
    switch (changeType) {
      case ChangeType.STYLING:
        return new StylingChange(changeFields, id);
      case ChangeType.ADD_JSX:
        return new AddJsxChange(changeFields, id);
      case ChangeType.REMOVE_JSX:
        return new RemoveJsxChange(changeFields, id);
      case ChangeType.MOVE_JSX:
        return new MoveJsxChange(changeFields, id);
      case ChangeType.CHANGE_PROP:
        return new ChangePropChange(changeFields, id);
      case ChangeType.ADD_CLASS:
        return new AddClassChange(changeFields, id);
      case ChangeType.REMOVE_CLASS:
        return new RemoveClassChange(changeFields, id);
      case ChangeType.UPDATE_CLASSES:
        return new UpdateClassesChange(changeFields, id);
      case ChangeType.WRAP_DIV:
        return new WrapDivChange(changeFields, id);
      case ChangeType.CHANGE_TAG:
        return new ChangeTagChange(changeFields, id);
      case ChangeType.DUPLICATE:
        return new DuplicateChange(changeFields, id);
      case ChangeType.EDIT_TEXT:
        return new EditTextChange(changeFields, id);
      case ChangeType.UNDO:
        changeFields.changeToUndo = reconstructChangeLedgerClass(
          changeFields.changeToUndo
        );
        return new UndoChange(changeFields, id);
      case ChangeType.REDO:
        changeFields.changeToRedo = reconstructChangeLedgerClass(
          changeFields.changeToRedo
        );
        return new RedoChange(changeFields, id);
      case ChangeType.UNKNOWN:
        return new UnknownChange(changeFields, id);
      default:
        throw new Error(`Unknown change type: ${changeType}`);
    }
  };

  // Set all the other fields on the change object
  const change = getChangeForType();
  Object.keys(plainJsObject).forEach((key: string) => {
    if (["type", "changeFields", "id"].includes(key)) {
      return;
    }

    // @ts-ignore
    change[key] = plainJsObject[key];
  });

  return change;
};
